import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { getCountryByCode } from '@adornis/internationalization/helpers/flag.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { searchParamsSignal } from '@adornis/router/client/routing-state-signal.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { EntityVersions } from '@adornis/versioned-entity/client/EntityVersions.js';
import { format } from 'date-fns';
import { html, nothing } from 'lit';
import { of } from 'rxjs';
import * as m from '../../../../localization/out/messages.js';
import {
  confirmInterviewAttendance,
  subscribeNumberOfFreeSlotsAvailable,
  updateJobApplicationStatus,
  subscribeJobApplicationByID,
} from '../../../api/job-application.js';
import { getOrCalculateMatchingScore } from '../../../api/matching-score.js';
import { getAndEnsureUserDataOfUserID } from '../../../api/user.js';
import { MatchingWeekRole } from '../../../db/enums/roles.js';
import { JobApplicationStatus } from '../../../db/job-application.js';
import { MatchingScore } from '../../../db/MatchingScore.js';
import { ResumeFile } from '../../../db/ResumeFile.js';
import { MWUserData } from '../../../db/user.js';
import { mainRouter } from '../../mainRouter.js';
import { translateEnum } from '../../translate-enum.js';
import { getResumeFileByID } from '../../../api/resume-file.js';

/**
 * Back-button helper. If the URL has a `?returnTo=…` param the caller knows
 * exactly where to land us (used by /admin/user/<id> → application). Otherwise
 * fall back to the in-app router history (default behaviour for /company/…
 * flows that just want "previous page").
 */
const goBackOrReturnTo = () => {
  const returnTo = searchParamsSignal.value.get('returnTo');
  if (returnTo) goTo(returnTo);
  else mainRouter.goBack('history');
};

export const CompanyApplicationDetail = stateful((props: { applicationID: string }) => {
  // The same component is rendered on both /admin/... and /company/... routes.
  // Companies should not see the applicant's email; admins still need it.
  const viewer = useObservable(() => currentUser<User>(), []);
  const isAdmin = viewer?.roles?.some(r => r.name === MatchingWeekRole.ADMIN) ?? false;

  const application = useQuery(
    () =>
      subscribeJobApplicationByID(props.applicationID)({
        _id: 1,
        status: 1,
        jobPositionId: 1,
        createdAt: 1,
        updatedAt: 1,
        interviewStart: 1,
        interviewAttended: 1,
        user: selectionSet(() => User, 1),
      }),
    [props.applicationID],
  );

  const userDataQuery = useQuery(() => {
    const userID = application.value?.user?._id;
    if (!userID) return of(null);
    return getAndEnsureUserDataOfUserID(userID)(selectionSet(() => MWUserData, 2));
  }, [application.value?.user?._id]);

  const resumeFileQuery = useQuery(
    () =>
      userDataQuery.value?.resumeFileId
        ? getResumeFileByID(userDataQuery.value.resumeFileId)({ _id: 1, originalName: 1 })
        : of(null),
    [userDataQuery.value?.resumeFileId],
  );

  const numberOfFreeSlotsRemaining = useQuery(
    () =>
      application.value?.jobPositionId
        ? subscribeNumberOfFreeSlotsAvailable(application.value?.jobPositionId)()
        : of(0),
    [application.value?.jobPositionId],
  );
  if (numberOfFreeSlotsRemaining.loading || application.loading)
    return html`<div class="p-8 text-center"><span class="loading loading-lg"></span></div>`;
  if (numberOfFreeSlotsRemaining.value === undefined || numberOfFreeSlotsRemaining.value === null)
    return 'error while loading open slots';
  const hasFreeSlots = numberOfFreeSlotsRemaining.value > 0;

  if (!application.value) return nothing;

  const app = application.value;
  if (!app.user) return 'no app user';
  if (!app.jobPositionId) return 'no app job position id';

  const matchingScore = useQuery<MatchingScore | null>(
    () => getOrCalculateMatchingScore(app.user!._id, app.jobPositionId!)(selectionSet(() => MatchingScore, 2)),
    [app.user._id, app.jobPositionId],
  );

  const scoreDetails = matchingScore.loading
    ? html`<div class="p-8 text-center"><span class="loading loading-lg"></span></div>`
    : html`
        <div class="flex flex-col gap-4">
          <h2 class="card-title text-2xl mb-6">${m.matching_score_details_title()}</h2>

          <div class="flex flex-col md:flex-row gap-6">
            <!-- Total score with radial progress -->
            <div class="flex flex-col items-center">
              <div
                class="radial-progress text-primary"
                style="--value:${matchingScore.value?.total ?? 0}; --size:8rem;"
              >
                <span class="text-2xl font-bold">${(matchingScore.value?.total ?? 0).toFixed(0)}%</span>
              </div>
              <div class="mt-2 font-semibold text-center">${m.matching_score_total()}</div>
            </div>

            <!-- Category scores -->
            <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 flex-1">
              <div class="flex flex-col">
                <div class="font-semibold">${m.matching_score_category_professional_fit()}</div>
                <div class="flex items-center gap-2">
                  <progress
                    class="progress progress-primary w-full"
                    value="${(matchingScore.value?.rawSkills ?? 0) * 100}"
                    max="100"
                  ></progress>
                  <span class="text-sm">${((matchingScore.value?.rawSkills ?? 0) * 100).toFixed(0)}%</span>
                </div>
              </div>

              <div class="flex flex-col">
                <div class="font-semibold">${m.matching_score_category_language_skills()}</div>
                <div class="flex items-center gap-2">
                  <progress
                    class="progress progress-primary w-full"
                    value="${(matchingScore.value?.rawLanguages ?? 0) * 100}"
                    max="100"
                  ></progress>
                  <span class="text-sm">${((matchingScore.value?.rawLanguages ?? 0) * 100).toFixed(0)}%</span>
                </div>
              </div>

              <div class="flex flex-col">
                <div class="font-semibold">${m.matching_score_category_soft_skills()}</div>
                <div class="flex items-center gap-2">
                  <progress
                    class="progress progress-primary w-full"
                    value="${(matchingScore.value?.rawSoftSkills ?? 0) * 100}"
                    max="100"
                  ></progress>
                  <span class="text-sm">${((matchingScore.value?.rawSoftSkills ?? 0) * 100).toFixed(0)}%</span>
                </div>
              </div>

              <div class="flex flex-col">
                <div class="font-semibold">${m.matching_score_category_practical_experience()}</div>
                <div class="flex items-center gap-2">
                  <progress
                    class="progress progress-primary w-full"
                    value="${(matchingScore.value?.rawExperience ?? 0) * 100}"
                    max="100"
                  ></progress>
                  <span class="text-sm">${((matchingScore.value?.rawExperience ?? 0) * 100).toFixed(0)}%</span>
                </div>
              </div>

              <div class="flex flex-col">
                <div class="font-semibold">${m.matching_score_category_role_fit()}</div>
                <div class="flex items-center gap-2">
                  <progress
                    class="progress progress-primary w-full"
                    value="${(matchingScore.value?.rawPosition ?? 0) * 100}"
                    max="100"
                  ></progress>
                  <span class="text-sm">${((matchingScore.value?.rawPosition ?? 0) * 100).toFixed(0)}%</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Notes section -->
          ${
            matchingScore.value?.notes?.length
              ? html`
                  <div class="mt-4">
                    <h3 class="font-semibold text-lg mb-2">${m.notes_label()}</h3>
                    <ul class="list-disc list-inside space-y-1">
                      ${matchingScore.value.notes.map(note => html`<li class="text-sm">${note}</li>`)}
                    </ul>
                  </div>
                `
              : nothing
          }
        </div>
      `;

  return html`
    <div class="p-4 sm:p-8">
      <div class="flex flex-col justify-between items-center mb-6">
        <button @click=${() => goBackOrReturnTo()} class="btn btn-ghost">
          <span class="icon-[material-symbols--arrow-back]"></span>
          ${m.back()}
        </button>
        <h1 class="text-3xl font-bold text-base-content">${m.application_title()}</h1>
      </div>

      <div class="card bg-base-100 shadow-xl">
        <div class="card-body">
          <div class="flex items-center justify-between mb-4">
            <h2 class="card-title text-2xl">${m.details()}</h2>
            ${
              app._id
                ? html`
                    <button
                      class="btn btn-ghost"
                      title="${m.show_history_tooltip()}"
                      @click=${() =>
                        (document.getElementById(`application-history-${app._id}`) as HTMLDialogElement)?.showModal()}
                    >
                      <span class="icon-[material-symbols--history]"></span>
                      ${m.history()}
                    </button>
                  `
                : nothing
            }
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            ${
              isAdmin
                ? html`<div>
                    <div class="font-semibold">${m.email()}</div>
                    <div>${app.user?.email ?? '-'}</div>
                  </div>`
                : nothing
            }
            <div>
              <div class="font-semibold">${m.name()}</div>
              <div>${[app.user?.firstName, app.user?.lastName].filter(Boolean).join(' ') || '-'}</div>
            </div>
            ${
              isAdmin
                ? html`
                    <div>
                      <div class="font-semibold">${m.cv_extract_form_dateOfBirth_label()}</div>
                      <div>
                        ${
                          userDataQuery.value?.dateOfBirth
                            ? format(new Date(userDataQuery.value.dateOfBirth), 'dd.MM.yyyy')
                            : '-'
                        }
                      </div>
                    </div>
                    <div>
                      <div class="font-semibold">${m.cv_extract_form_nationality_label()}</div>
                      <div>
                        ${
                          userDataQuery.value?.nationality
                            ? getCountryByCode(userDataQuery.value.nationality, 'de')
                            : '-'
                        }
                      </div>
                    </div>
                    <div>
                      <div class="font-semibold">${m.cv_extract_form_countryOfBirth_label()}</div>
                      <div>
                        ${
                          userDataQuery.value?.countryOfBirth
                            ? getCountryByCode(userDataQuery.value.countryOfBirth, 'de')
                            : '-'
                        }
                      </div>
                    </div>
                    <div>
                      <div class="font-semibold">${m.cv_extract_form_hasMigrationBackground_label()}</div>
                      <div>
                        ${
                          userDataQuery.value?.hasMigrationBackground === true
                            ? m.yes()
                            : userDataQuery.value?.hasMigrationBackground === false
                              ? m.no()
                              : '-'
                        }
                      </div>
                    </div>
                  `
                : nothing
            }
            <div>
              <div class="font-semibold">${m.status()}</div>
              <div>${translateEnum('application_status', app.status ?? JobApplicationStatus.PENDING)}</div>
            </div>
            <div>
              <div class="font-semibold">${m.submitted()}</div>
              <div>${app.createdAt ? format(new Date(app.createdAt), 'dd.MM.yyyy, HH:mm') : '-'}</div>
            </div>
            <div>
              <div class="font-semibold">${m.updated()}</div>
              <div>${app.updatedAt ? format(new Date(app.updatedAt), 'dd.MM.yyyy, HH:mm') : '-'}</div>
            </div>
            <div>
              <div class="font-semibold">${m.appointment_booked_for()}</div>
              <div>${app.interviewStart ? format(new Date(app.interviewStart), 'dd.MM.yyyy, HH:mm') : '-'}</div>
            </div>
          </div>

          ${
            app.status === JobApplicationStatus.BOOKED &&
            app.interviewStart &&
            new Date(app.interviewStart) < new Date()
              ? html`
                  <div class="divider my-6"></div>
                  <div class="flex flex-col gap-3">
                    <h2 class="card-title text-2xl">${m.interview_attendance_title()}</h2>
                    <p class="text-base-content/70">${m.interview_attendance_question()}</p>
                    <div class="flex items-center gap-3 flex-wrap">
                      <button
                        class="btn ${app.interviewAttended === true ? 'btn-success' : 'btn-outline btn-success'}"
                        @click=${async () => {
                          try {
                            await confirmInterviewAttendance(app._id!, app.interviewAttended === true ? null : true)();
                            XSnackbar.show(m.interview_attendance_saved());
                          } catch (err) {
                            console.error('Failed to confirm interview attendance', err);
                            XSnackbar.show(m.interview_attendance_error());
                          }
                        }}
                      >
                        <span class="icon-[material-symbols--check]"></span>
                        ${m.yes()}
                      </button>
                      <button
                        class="btn ${app.interviewAttended === false ? 'btn-error' : 'btn-outline btn-error'}"
                        @click=${async () => {
                          try {
                            await confirmInterviewAttendance(
                              app._id!,
                              app.interviewAttended === false ? null : false,
                            )();
                            XSnackbar.show(m.interview_attendance_saved());
                          } catch (err) {
                            console.error('Failed to confirm interview attendance', err);
                            XSnackbar.show(m.interview_attendance_error());
                          }
                        }}
                      >
                        <span class="icon-[material-symbols--close]"></span>
                        ${m.no()}
                      </button>
                      <span class="text-sm text-base-content/60">
                        ${
                          app.interviewAttended == null
                            ? m.interview_attendance_state_pending()
                            : app.interviewAttended
                              ? m.interview_attendance_state_yes()
                              : m.interview_attendance_state_no()
                        }
                      </span>
                    </div>
                  </div>
                `
              : nothing
          }

          <div class="divider my-6"></div>

          ${scoreDetails}

          <div class="divider my-6"></div>

          <div class="flex items-center justify-between mb-4 mt-6">
            <h2 class="card-title text-2xl">${m.user_data()}</h2>
            <div class="flex items-center gap-2">
              ${
                userDataQuery.value?.resumeFileId
                  ? html`
                      <a
                        class="btn btn-outline btn-sm"
                        title="${m.resume_label()}"
                        target="_blank"
                        rel="noopener"
                        href="${ResumeFile.getServeLink(userDataQuery.value.resumeFileId)}"
                        aria-label="${m.resume_label()}"
                      >
                        <span class="icon-[material-symbols--link]"></span>
                        ${(resumeFileQuery.value as any)?.originalName ?? `${m.resume_label()} (PDF)`}
                      </a>
                    `
                  : nothing
              }
              ${
                userDataQuery.value?._id
                  ? html`
                      <button
                        class="btn btn-ghost"
                        title="${m.show_history_tooltip()}"
                        @click=${() =>
                          (
                            document.getElementById(`userdata-history-${userDataQuery.value?._id}`) as HTMLDialogElement
                          )?.showModal()}
                      >
                        <span class="icon-[material-symbols--history]"></span>
                        ${m.history()}
                      </button>
                    `
                  : nothing
              }
            </div>
          </div>
          ${
            userDataQuery.loading
              ? html`<div class="p-4 text-center"><span class="loading loading-sm"></span></div>`
              : userDataQuery.value
                ? html`
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <div class="font-semibold">${m.cv_extract_form_university_label()}</div>
                        <div>${translateEnum('cv_extract_form_enum', userDataQuery.value.university ?? '-')}</div>
                      </div>
                      <div>
                        <div class="font-semibold">${m.cv_extract_form_studyField_label()}</div>
                        <div>${userDataQuery.value.studyField ?? '-'}</div>
                      </div>
                      <div>
                        <div class="font-semibold">${m.cv_extract_form_department_label()}</div>
                        <div>${translateEnum('cv_extract_form_enum', userDataQuery.value.department ?? '-')}</div>
                      </div>
                      <div>
                        <div class="font-semibold">${m.cv_extract_form_academicTitle_label()}</div>
                        <div>${translateEnum('cv_extract_form_enum', userDataQuery.value.academicTitle ?? '-')}</div>
                      </div>
                      <div>
                        <div class="font-semibold">${m.cv_extract_form_expectedCompletionDate_label()}</div>
                        <div>
                          ${
                            userDataQuery.value.expectedCompletionDate
                              ? format(new Date(userDataQuery.value.expectedCompletionDate), 'dd.MM.yyyy')
                              : '-'
                          }
                        </div>
                      </div>
                      <div class="col-start-1">
                        <div class="font-semibold">${m.german_level_label()}</div>
                        <div>${translateEnum('cv_extract_form_enum', userDataQuery.value.languageSkillsDE ?? '-')}</div>
                      </div>
                      <div>
                        <div class="font-semibold">${m.english_level_label()}</div>
                        <div>${translateEnum('cv_extract_form_enum', userDataQuery.value.languageSkillsEN ?? '-')}</div>
                      </div>
                      <div class="md:col-span-2">
                        <div class="font-semibold">${m.other_languages_label()}</div>
                        <div>
                          ${
                            (userDataQuery.value.otherLanguages ?? [])
                              .map(l =>
                                `${l.language ?? ''}${
                                  l.level ? ` (${translateEnum('cv_extract_form_enum', l.level)})` : ''
                                }`.trim(),
                              )
                              .filter(Boolean)
                              .join(', ') || '-'
                          }
                        </div>
                      </div>
                      <div class="md:col-span-2">
                        <div class="font-semibold">${m.register_onboarding_personal_info_it_skills_title()}</div>
                        <div class="whitespace-pre-wrap">${userDataQuery.value.techSkills ?? '-'}</div>
                      </div>
                      <div class="md:col-span-2">
                        <div class="font-semibold">${m.soft_skills()}</div>
                        <div>
                          ${
                            (userDataQuery.value.softSkills ?? [])
                              .map(ss => translateEnum('job_form_enum', ss))
                              .join(', ') || '-'
                          }
                        </div>
                      </div>
                      <div class="md:col-span-2">
                        <div class="font-semibold">${m.practical_experience()}</div>
                        <div class="space-y-1">
                          ${
                            (userDataQuery.value.practicalExperiences ?? []).length > 0
                              ? html`
                                  <ul class="list-disc list-inside">
                                    ${(userDataQuery.value.practicalExperiences ?? [])
                                      .map(
                                        pe =>
                                          html`<li>
                                            ${pe.description ?? ''}${
                                              pe.level ? ` (${translateEnum('cv_extract_form_enum', pe.level)})` : ''
                                            }
                                          </li>`,
                                      )
                                      .filter(Boolean)}
                                  </ul>
                                `
                              : '-'
                          }
                        </div>
                      </div>
                      <div class="md:col-span-2">
                        <div class="font-semibold">${m.desired_employment_types_label()}</div>
                        <div>
                          ${
                            (userDataQuery.value.desiredEmploymentTypes ?? [])
                              .map(e => translateEnum('job_form_enum', e))
                              .join(', ') || '-'
                          }
                        </div>
                      </div>
                      <div class="md:col-span-2">
                        <div class="font-semibold">${m.company_application_motivation_label()}</div>
                        <p>
                          ${
                            userDataQuery.value?.motivations?.find(m => m.jobPositionID === app.jobPositionId)
                              ?.motivation ?? '-'
                          }
                        </p>
                      </div>
                    </div>
                  `
                : html`<div class="opacity-60">${m.no_user_profile_data()}</div>`
          }
          ${
            [JobApplicationStatus.ACCEPTED, JobApplicationStatus.BOOKED, JobApplicationStatus.REJECTED].includes(
              app.status ?? JobApplicationStatus.PENDING,
            )
              ? html`
                  <div class="alert alert-info">
                    <span>
                      ${
                        app.status === JobApplicationStatus.ACCEPTED || app.status === JobApplicationStatus.BOOKED
                          ? m.application_already_accepted()
                          : m.application_already_declined()
                      }
                    </span>
                  </div>
                  <div class="flex justify-end gap-2">
                    ${
                      app.status === JobApplicationStatus.REJECTED
                        ? html`<button
                              class="btn btn-success ${
                                hasFreeSlots ? '' : 'text-base-content/30 btn-disabled opacity-50 cursor-default'
                              }"
                              @click=${async () => {
                                try {
                                  const confirmAccept = confirm(m.accept_confirm());
                                  if (!confirmAccept) return;
                                  await updateJobApplicationStatus(app._id!, JobApplicationStatus.ACCEPTED)();
                                  goBackOrReturnTo();
                                } catch (err) {
                                  console.error('Failed to accept application', err);
                                  XSnackbar.show(m.accept_error());
                                }
                              }}
                            >
                              ${m.accept()}
                            </button>
                            <button
                              class="btn btn-warning"
                              @click=${async () => {
                                try {
                                  const confirmWaitingList = confirm(m.waiting_list_confirm());
                                  if (!confirmWaitingList) return;
                                  await updateJobApplicationStatus(app._id!, JobApplicationStatus.WAITING)();
                                  goBackOrReturnTo();
                                } catch (err) {
                                  console.error('Failed to add application to waiting list', err);
                                  XSnackbar.show(m.waiting_list_error());
                                }
                              }}
                            >
                              ${m.waiting_list()}
                            </button>`
                        : html` <button
                            class="btn btn-outline"
                            @click=${async () => {
                              try {
                                const confirmRemove = confirm(m.remove_participation_confirm());
                                if (!confirmRemove) return;
                                await updateJobApplicationStatus(app._id!, JobApplicationStatus.REJECTED)();
                                goBackOrReturnTo();
                              } catch (err) {
                                console.error('Failed to remove application from participation', err);
                                alert(m.remove_participation_error());
                              }
                            }}
                          >
                            ${m.remove_from_participation()}
                          </button>`
                    }
                  </div>
                `
              : html`
                  <div class="flex justify-end gap-2">
                    <button
                      class="btn btn-success ${
                        hasFreeSlots ? '' : 'text-base-content/30 btn-disabled opacity-50 cursor-default'
                      }"
                      @click=${async () => {
                        try {
                          await updateJobApplicationStatus(app._id!, JobApplicationStatus.ACCEPTED)();
                          goBackOrReturnTo();
                          XSnackbar.show(m.accept());
                        } catch (err) {
                          console.error('Failed to accept application', err);
                          XSnackbar.show(m.accept_error());
                        }
                      }}
                    >
                      ${m.accept()}
                    </button>
                    <button
                      class="btn btn-error"
                      @click=${async () => {
                        try {
                          await updateJobApplicationStatus(app._id!, JobApplicationStatus.REJECTED)();
                          goBackOrReturnTo();
                          XSnackbar.show(m.decline());
                        } catch (err) {
                          console.error('Failed to decline application', err);
                          XSnackbar.show(m.decline_error());
                        }
                      }}
                    >
                      ${m.decline()}
                    </button>
                  </div>
                `
          }
        </div>
      </div>
      ${
        app._id
          ? html`
              <dialog id="application-history-${app._id}" class="modal">
                <div class="modal-box min-w-[800px]">
                  <h3 class="font-bold text-lg mb-4">${m.application_history_title()}</h3>
                  <div class="max-h-[70vh] overflow-auto">${EntityVersions({ documentID: app._id })}</div>
                </div>
                <form method="dialog" class="modal-backdrop">
                  <button>close</button>
                </form>
              </dialog>
            `
          : nothing
      }
      ${
        userDataQuery.value?._id
          ? html`
              <dialog id="userdata-history-${userDataQuery.value._id}" class="modal">
                <div class="modal-box min-w-[800px]">
                  <h3 class="font-bold text-lg mb-4">${m.user_data_history_title()}</h3>
                  <div class="max-h-[70vh] overflow-auto">
                    ${EntityVersions({ documentID: userDataQuery.value._id })}
                  </div>
                </div>
                <form method="dialog" class="modal-backdrop">
                  <button>close</button>
                </form>
              </dialog>
            `
          : nothing
      }
    </div>
  `;
});
